import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, RefObject, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandler, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {splitIntoVisibleAndRemainingRows} from '../../../../srcCommon/inboxes/contactDetails/contactDetailsHelpers';
import {splitConversationsIntoRecentAndOlder} from '../../../helpers/models/conversationHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {ContactOrAccountDetailsPanelId} from '../../../store/panels/panelStateBase';
import {AsyncLoader} from '../../common/loaders/asyncLoader';
import {highlightTotalTime} from './conversationCard';
import {ConversationCardContainer} from './conversationCardContainer';
import {ConversationListSkeleton} from './skeleton/conversationListSkeleton';

/*
 * Types.
 */

interface ConversationListProps {
  panelId: ContactOrAccountDetailsPanelId;
  conversations: Array<ConversationModel>;
  hasMoreConversations: boolean | undefined;
  conversationLoadingStatus: AsyncStatusesEnum | undefined;
  conversationIdsToHighlight: Array<number>;
  conversationsRef?: RefObject<HTMLDivElement>;

  fetchConversations: AsyncHandler;
  onConversationClick: HandlerOf<ConversationModel>;
  searchHistory?: Handler;
  markHighlightAsViewed?: Handler;
  onConversationHoverTrack?: Handler;
}

enum ConversationListRowTypesEnum {
  SUBTITLE = 'SUBTITLE',
  CONVERSATION = 'CONVERSATION',
}

interface ConversationListRow {
  type: ConversationListRowTypesEnum;
  content: ReactNode;
}

/*
 * Style.
 */

const StyledConversationListDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const StyledSubtitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  margin: 12px 0;

  &:first-child {
    margin-top: 3px;
  }
`;

const StyledButtonContainerDiv = styled.div`
  display: flex;
`;

const StyledButton = styled(Button)`
  position: relative;
  left: -12px;
`;

const StyledErrorDiv = styled.div`
  padding: 32px 0;
`;

const StyledNoConversationsDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin: -4px 0 4px 0;
`;

/*
 * Component.
 */

export const ConversationList: FC<ConversationListProps> = (props) => {
  const {
    panelId,
    conversations,
    hasMoreConversations,
    conversationLoadingStatus,
    conversationIdsToHighlight,
    conversationsRef: passedInRef,
    searchHistory,
    markHighlightAsViewed,
    fetchConversations,
    onConversationHoverTrack,
    onConversationClick,
  } = props;
  const [isShowingMore, setIsShowingMore] = useState(false);
  const showMore = () => {
    setIsShowingMore(true);
  };
  const conversationListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (conversationIdsToHighlight.length > 0) {
      // The conversation list appears in the middle of the contact details panel so we want to scroll the user to it
      if (conversationListRef?.current) {
        conversationListRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
      }
      // We may have a passed in ref, so scroll to that if we have it defined.
      if (passedInRef?.current) {
        passedInRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
      }

      // After the highlight has run, we want to store that the user has seen the highlighted conversations
      // so we don't show the highlights again (until the next time they see the recent communications banner)
      if (markHighlightAsViewed) {
        setTimeout(() => {
          markHighlightAsViewed();
        }, highlightTotalTime);
      }
    }
  }, [markHighlightAsViewed, conversationIdsToHighlight, passedInRef]);

  // Check if we have the conversations loaded, if we dont fetch them.
  useEffect(() => {
    async function loadConversations() {
      if (!conversationLoadingStatus) {
        setIsShowingMore(false);
        await fetchConversations();
      }
    }
    loadConversations();
  }, [conversationLoadingStatus, fetchConversations]);

  // Render loading screen if we do not have any conversations
  if (!conversationLoadingStatus || conversationLoadingStatus === AsyncStatusesEnum.LOADING) {
    return <ConversationListSkeleton />;
  }
  // If we failed to load a conversation, show retry
  if (conversationLoadingStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorDiv>
        <AsyncLoader
          size={VisualSizesEnum.LARGE}
          status={AsyncStatusesEnum.FAILED}
          error={<Trans id="conversation-list-error-loading-conversations">Something went wrong.</Trans>}
          onRetryClick={() => {
            fetchConversations();
          }}
        />
      </StyledErrorDiv>
    );
  }

  const {visibleRows, remainingRows} = computeVisibleAndRemainingRows(
    panelId,
    conversations,
    conversationIdsToHighlight,
    onConversationClick,
    onConversationHoverTrack,
  );
  const isMoreRemaining = remainingRows.length > 0;

  return (
    <StyledConversationListDiv ref={passedInRef ?? conversationListRef}>
      {visibleRows.map((row) => row.content)}
      {isMoreRemaining && !isShowingMore && renderShowMoreButton(showMore)}
      {isMoreRemaining && isShowingMore && (
        <>
          {remainingRows.map((row) => row.content)}
          {hasMoreConversations && renderSearchAllHistoryButton(searchHistory)}
        </>
      )}
      {visibleRows.length === 0 && renderNoConversations()}
    </StyledConversationListDiv>
  );
};

function renderShowMoreButton(showMore: Handler) {
  return (
    <StyledButtonContainerDiv>
      <StyledButton variant={ButtonVariantsEnum.LINK} onClick={showMore}>
        <Trans id="contact-details-conversation-list-show-more">Show more</Trans>
      </StyledButton>
    </StyledButtonContainerDiv>
  );
}

function renderSearchAllHistoryButton(searchHistory?: Handler) {
  if (!searchHistory) {
    return null;
  }
  return (
    <StyledButtonContainerDiv>
      <StyledButton variant={ButtonVariantsEnum.LINK} onClick={searchHistory}>
        <Trans id="contact-details-conversation-list-search-all-history">Search all history</Trans>
      </StyledButton>
    </StyledButtonContainerDiv>
  );
}

function renderNoConversations() {
  return (
    <StyledNoConversationsDiv>
      <Trans id="contact-details-conversation-list-no-conversations">No conversations</Trans>
    </StyledNoConversationsDiv>
  );
}

/*
 * Helpers.
 */

function computeVisibleAndRemainingRows(
  panelId: ContactOrAccountDetailsPanelId,
  conversations: ReadonlyArray<ConversationModel>,
  conversationIdsToHighlight: Array<number>,
  onConversationClick: HandlerOf<ConversationModel>,
  onConversationHoverTrack?: Handler,
) {
  const [recentConversations, olderConversations] = splitConversationsIntoRecentAndOlder(conversations);
  const rows = buildRows(
    panelId,
    recentConversations,
    olderConversations,
    conversationIdsToHighlight,
    onConversationClick,
    onConversationHoverTrack,
  );

  return splitIntoVisibleAndRemainingRows(rows);
}

function buildRows(
  panelId: ContactOrAccountDetailsPanelId,
  recentConversations: ReadonlyArray<ConversationModel>,
  olderConversations: ReadonlyArray<ConversationModel>,
  conversationIdsToHighlight: Array<number>,
  onConversationClick: HandlerOf<ConversationModel>,
  onConversationHoverTrack?: Handler,
): Array<ConversationListRow> {
  // If there are any recent/older conversations, we want to show the respective subtitles
  // If there are only recent conversations, we want to show the 'Last 7 days' subtitle
  // If there are only older conversations, we don't want to show any subtitles
  const hasRecentConversations = recentConversations.length > 0;
  const hasOlderConversations = olderConversations.length > 0;

  return [
    buildSubtitleRow(
      hasRecentConversations,
      <StyledSubtitleDiv key="last-7-days">
        <Trans id="contact-details-conversation-list-last-7-days">Last 7 days</Trans>
      </StyledSubtitleDiv>,
    ),
    ...recentConversations.map((conversation) =>
      buildConversationRow(
        panelId,
        conversation,
        conversationIdsToHighlight.includes(conversation.id),
        onConversationClick,
        onConversationHoverTrack,
      ),
    ),
    buildSubtitleRow(
      hasRecentConversations && hasOlderConversations,
      <StyledSubtitleDiv key="older">
        <Trans id="contact-details-conversation-list-older">Older</Trans>
      </StyledSubtitleDiv>,
    ),
    ...olderConversations.map((conversation) =>
      buildConversationRow(
        panelId,
        conversation,
        conversationIdsToHighlight.includes(conversation.id),
        onConversationClick,
        onConversationHoverTrack,
      ),
    ),
  ].filter(isDefined);
}

function buildSubtitleRow(shouldRender: boolean, subtitleContent: ReactNode) {
  if (!shouldRender) {
    return undefined;
  }

  return {
    type: ConversationListRowTypesEnum.SUBTITLE,
    content: subtitleContent,
  };
}

function buildConversationRow(
  panelId: ContactOrAccountDetailsPanelId,
  conversation: ConversationModel,
  shouldHighlight: boolean,
  onClick: HandlerOf<ConversationModel>,
  onConversationHoverTrack?: Handler,
) {
  return {
    type: ConversationListRowTypesEnum.CONVERSATION,
    content: (
      <ConversationCardContainer
        key={conversation.id}
        panelId={panelId}
        conversation={conversation}
        shouldHighlight={shouldHighlight}
        onClick={onClick}
        onConversationHoverTrack={onConversationHoverTrack}
      />
    ),
  };
}
